import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiShield, FiAlertTriangle, FiAlertCircle, FiEdit3, FiDownload, FiChevronDown, FiChevronRight, FiLock, FiXCircle, FiDollarSign, FiTrendingUp, FiCheckCircle, FiFlag } from 'react-icons/fi';
import type { IconType } from 'react-icons';
import { IoIosRefresh } from 'react-icons/io';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import Cpc25BarChart from './shared/Cpc25BarChart';
import Cpc25DonutChart from './shared/Cpc25DonutChart';
import ProvisionPeriodModal from './shared/ProvisionPeriodModal';
import { HelpButton } from './shared/SectionHelp';
import downloadCsv from './shared/downloadCsv';
import useCpc25Report from '../../../hooks/useCpc25Report';
import { buildCpc25Csv, cpc25CsvFilename } from './shared/cpc25Export';
import { provisionByEntity, casesByClassification } from './shared/cpc25Charts';
import { attentionReasons, primarySignal, rowSignals, CRITICAL_SIGNALS, type AttentionSignal } from './shared/cpc25Attention';
import { recoveryApi } from '../../../services/recoveryApi';
import { CPC25_UNCLASSIFIED } from '../../../types/LegalCase';
import type {
    Cpc25AttentionCase,
    Cpc25Report,
    Cpc25ReportEntity,
    Cpc25ReportPole,
} from '../../../types/LegalCase';

// Relatório consolidado CPC 25 (Jurídico → Relatório CPC 25). Equivalente calculado da aba
// VALORES da planilha: por entidade credora, Ativo/Passivo lado a lado, tabela Classificação ×
// [Nº, Perda estimada, Provisão] + totais, mais gráficos de provisão por entidade e de
// distribuição de casos por classificação. Read-only, com exportação CSV. A provisão já vem
// calculada pela regra do CPC 25 no backend (só passivo provável).

// Ícone e cor de cada motivo de atenção (Task 22). Vermelho = infla a provisão hoje; âmbar = põe a
// base em dúvida ou é desvio do padrão; cinza = ajuste manual, que é legítimo.
const SIGNAL_ICON: Record<AttentionSignal, IconType> = {
    closedStillProvisioning: FiXCircle,
    allTitlesSettled: FiCheckCircle,
    paidStillProvisioning: FiDollarSign,
    overrideAboveCalculated: FiTrendingUp,
    claimValueDiverges: FiAlertCircle,
    creditorEntityUndefined: FiFlag,
    overridden: FiEdit3,
    offSuggestion: FiAlertTriangle,
};

const signalColor = (signal: AttentionSignal): string => {
    if (CRITICAL_SIGNALS.has(signal)) return 'text-red-500 dark:text-red-400';
    if (signal === 'overridden') return 'text-gray-400 dark:text-gray-500';
    return 'text-amber-500 dark:text-amber-400';
};

const SignalIcon = ({ signal, size, title }: { signal: AttentionSignal; size: number; title?: string }) => {
    const Icon = SIGNAL_ICON[signal];
    return <Icon className={`${signalColor(signal)} shrink-0`} size={size} title={title} />;
};

// Uma tabela de polo (classificações + subtotais). Fora do componente de página.
// Cada linha é clicável: leva à grid de Casos filtrada por entidade × polo × classificação —
// exatamente o que a linha representa (AF-3691: "clica no Provável e vê quais casos são").
interface PoleTableProps {
    pole: Cpc25ReportPole;
    creditorEntity: string;
    onDrillDown: (creditorEntity: string, pole: string, classification: string | null) => void;
}

const PoleTable = ({ pole, creditorEntity, onDrillDown }: PoleTableProps) => (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{pole.poleFormatted}</span>
        </div>
        <table className="w-full text-sm">
            <thead>
                <tr className="text-xs text-gray-500 dark:text-gray-400">
                    <th className="py-2 px-3 text-left font-medium">Classificação</th>
                    <th className="py-2 px-3 text-right font-medium">Nº</th>
                    <th className="py-2 px-3 text-right font-medium">Perda estimada</th>
                    <th className="py-2 px-3 text-right font-medium">Provisão</th>
                </tr>
            </thead>
            <tbody>
                {pole.rows.map((row) => {
                    const label = `Ver os ${row.count} caso(s) de ${row.classificationFormatted} (${pole.poleFormatted})`;
                    const drill = () => onDrillDown(creditorEntity, pole.pole, row.classification);
                    return (
                        <tr key={`${pole.pole}-${row.classification ?? 'none'}`}
                            className="border-t border-gray-100 dark:border-slate-700 hover:bg-blue-50 dark:hover:bg-slate-700">
                            <td className="py-2 px-3 text-gray-700 dark:text-gray-300">
                                <span className="flex items-center gap-1.5">
                                    {/* Botão semântico na célula: focável e ativável por teclado
                                        (Enter/Espaço), diferente de um onClick no <tr>. Preserva a
                                        estrutura da tabela (apontamento de acessibilidade do Copilot). */}
                                    <button
                                        type="button"
                                        onClick={drill}
                                        title={label}
                                        aria-label={label}
                                        className="text-left text-blue-700 dark:text-blue-400 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                                    >
                                        {row.classificationFormatted}
                                    </button>
                                    {rowSignals(row).map(({ signal, title }) => (
                                        <SignalIcon key={signal} signal={signal} size={12} title={title} />
                                    ))}
                                </span>
                            </td>
                            <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-300">{row.count}</td>
                            <td className="py-2 px-3 text-right dark:text-gray-200">{row.estimatedLossFormatted || '—'}</td>
                            <td className="py-2 px-3 text-right font-medium dark:text-white">{row.provisionFormatted || 'R$ 0,00'}</td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900 font-medium">
                    <td className="py-2 px-3 text-gray-700 dark:text-gray-200">Total {pole.poleFormatted}</td>
                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{pole.count}</td>
                    <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">{pole.estimatedLossFormatted || '—'}</td>
                    <td className="py-2 px-3 text-right text-gray-900 dark:text-white">{pole.provisionFormatted || 'R$ 0,00'}</td>
                </tr>
            </tfoot>
        </table>
    </div>
);

interface EntityBlockProps {
    entity: Cpc25ReportEntity;
    onDrillDown: (creditorEntity: string, pole: string, classification: string | null) => void;
}

const EntityBlock = ({ entity, onDrillDown }: EntityBlockProps) => (
    <div className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{entity.creditorEntity}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {entity.poles.map((pole) => (
                <PoleTable
                    key={`${entity.creditorEntity}-${pole.pole}`}
                    pole={pole}
                    creditorEntity={entity.creditorEntity}
                    onDrillDown={onDrillDown}
                />
            ))}
        </div>
    </div>
);

// Card de total destacado. Cor de destaque opcional (usada na provisão) e tooltip explicativo.
const SummaryCard = ({ label, value, accent, hint }: { label: string; value: string; accent?: 'danger' | 'default'; hint?: string }) => (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4" title={hint}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`text-2xl font-semibold ${accent === 'danger'
            ? 'text-red-700 dark:text-red-300'
            : 'text-gray-900 dark:text-white'}`}>
            {value}
        </p>
    </div>
);


const AttentionIcon = ({ c }: { c: Cpc25AttentionCase }) => {
    const signal = primarySignal(c);
    if (!signal) return null;
    return <SignalIcon signal={signal} size={13} />;
};

// Linha da lista "Casos que merecem atenção".
const AttentionRow = ({ c, onOpen }: { c: Cpc25AttentionCase; onOpen: (id: string) => void }) => (
    <button
        type="button"
        onClick={() => onOpen(c.id)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700"
    >
        <span className="flex items-center gap-2 min-w-0">
            <AttentionIcon c={c} />
            <span className="truncate">
                <span className="font-mono text-xs text-blue-700 dark:text-blue-400">{c.externalCaseNumber || '(sem nº)'}</span>
                <span className="text-gray-500 dark:text-gray-400 text-xs"> • {c.creditorEntity} • {c.poleFormatted} • {c.classificationFormatted}{c.statusFormatted ? ` • ${c.statusFormatted}` : ''}</span>
            </span>
        </span>
        <span className="flex items-center gap-3 min-w-0 text-xs">
            <span className="text-gray-500 dark:text-gray-400 text-right break-words min-w-0" title={c.provisionOverrideReason || undefined}>
                {attentionReasons(c).join(' · ')}
                {c.overridden && c.provisionOverrideReason ? ' *' : ''}
            </span>
            <span className="text-gray-400 dark:text-gray-500 shrink-0">{c.estimatedLossPercentFormatted || '—'}</span>
        </span>
    </button>
);

// Seção expansível "Casos que merecem atenção", recolhida por padrão.
const AttentionSection = ({ cases, onOpen }: { cases: Cpc25AttentionCase[]; onOpen: (id: string) => void }) => {
    const [open, setOpen] = useState(false);
    if (cases.length === 0) return null;
    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700">
            <div className="flex items-center pr-3">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    className="flex-1 flex items-center gap-2 px-4 py-3 text-left dark:text-gray-200"
                >
                    {open ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Casos que merecem atenção
                    </span>
                    <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                        {cases.length}
                    </span>
                </button>
                <HelpButton topic="report.attention" label="Abrir ajuda: casos que merecem atenção" />
            </div>
            {open && (
                <div className="px-2 pb-2 space-y-0.5">
                    {cases.map((c) => <AttentionRow key={c.id} c={c} onOpen={onOpen} />)}
                </div>
            )}
        </div>
    );
};

// Dispara o download de um CSV vindo do backend (texto cru). Reusa o mesmo Blob+link.
const downloadTextCsv = downloadCsv;

const Cpc25ReportPage = () => {
    const r = useCpc25Report();
    const navigate = useNavigate();
    const [entityInput, setEntityInput] = useState('');
    const [showPeriodModal, setShowPeriodModal] = useState(false);
    const [isExportingCases, setIsExportingCases] = useState(false);

    const report: Cpc25Report = r.report;
    const provisionSegments = useMemo(() => provisionByEntity(report), [report]);
    const classificationSegments = useMemo(() => casesByClassification(report), [report]);

    const hasData = report.entities.length > 0;
    // Há filtro aplicado quando o hook está com uma entidade setada (o que já foi buscado),
    // não quando só há texto digitado ainda não confirmado.
    const hasActiveFilter = r.creditorEntity.trim() !== '';

    const applyFilter = () => r.setCreditorEntity(entityInput);

    // Drill-down (AF-3691): manda os três recortes da célula para a grid de Casos, que semeia
    // os filtros pela query string. Classificação nula = "Não classificado" — nesse caso só
    // entidade + polo vão, porque não há valor de classificação para filtrar.
    const drillDown = (
        creditorEntity: string,
        pole: string,
        classification: string | null,
    ) => {
        const params = new URLSearchParams({ creditorEntity, pole });
        // Classificação nula = "Não classificado" → manda o sentinela, que o backend traduz em
        // IS NULL. Sem ele, o parâmetro sumiria e a grid traria os classificados junto (bug).
        params.set('cpc25Classification', classification ?? CPC25_UNCLASSIFIED);
        navigate(`/recovery/legal-cases?${params.toString()}`);
    };

    const clearFilter = () => {
        setEntityInput('');
        r.setCreditorEntity('');
    };

    const onExport = () => {
        downloadCsv(buildCpc25Csv(report), cpc25CsvFilename());
    };

    // Export por caso (Task 20): uma linha por caso, gerado pelo backend (a auditoria confere
    // *quais* casos compõem cada número). Respeita o filtro de entidade aplicado.
    const onExportCases = async () => {
        setIsExportingCases(true);
        try {
            const csv = await recoveryApi.getCpc25CaseExportCsv({
                creditorEntity: r.creditorEntity || undefined,
            });
            downloadTextCsv(csv, `cpc25-por-caso-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (e: any) {
            toast.error(e?.message || 'Erro ao exportar por caso');
        } finally {
            setIsExportingCases(false);
        }
    };

    return (
        <>
            {r.isLoading && <Loading />}

            <div className="flex flex-col h-screen bg-white dark:bg-slate-800 overflow-hidden">
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <FiShield className="text-blue-600" size={20} />
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Relatório CPC 25</h1>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-56">
                                <TextEdit
                                    label="Filtrar por entidade (ex.: holding)"
                                    value={entityInput}
                                    onChange={(ev: any) => setEntityInput(ev.target.value)}
                                    onKeyDown={(ev: any) => { if (ev.key === 'Enter') applyFilter(); }}
                                />
                            </div>
                            <button onClick={applyFilter}
                                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                                title="Aplicar filtro">
                                Filtrar
                            </button>
                            {hasActiveFilter && (
                                <button onClick={clearFilter}
                                    className="px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium"
                                    title="Limpar filtro">
                                    Limpar
                                </button>
                            )}
                            <button onClick={onExport} disabled={!hasData}
                                className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                                title="Exportar o relatório AO VIVO, consolidado (entidade × polo × classificação). O fechamento congelado se exporta em Fechar período.">
                                <FiDownload size={14} /> Exportar ao vivo
                            </button>
                            <button onClick={onExportCases} disabled={!hasData || isExportingCases}
                                className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
                                title="Exportar o relatório AO VIVO, uma linha por caso. O fechamento congelado (o que vai à auditoria) se exporta em Fechar período.">
                                <FiDownload size={14} /> {isExportingCases ? 'Exportando...' : 'Por caso (ao vivo)'}
                            </button>
                            <button onClick={() => setShowPeriodModal(true)}
                                className="flex items-center gap-2 px-3 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium"
                                title="Fechar o período contábil (congela a provisão numa data-base)">
                                <FiLock size={14} /> Fechar período
                            </button>
                            <button onClick={r.refresh}
                                className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Atualizar">
                                <IoIosRefresh size={18} />
                            </button>
                            <HelpButton topic="report.totals" label="Abrir ajuda: os dois totais do relatório" />
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-auto p-4 space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                        <SummaryCard label="Casos" value={String(report.summary.count)} />
                        <SummaryCard
                            label="Perda estimada – polo passivo"
                            value={report.summary.estimatedLossPassiveFormatted || 'R$ 0,00'}
                            hint="Perda estimada dos casos em que a Agrolend é ré. É daqui que sai a provisão (só o provável provisiona). Polo passivo não é sinônimo de pagar: em embargos ou na RJ do devedor, perder é deixar de receber."
                        />
                        <SummaryCard
                            label="Perda estimada – polo ativo"
                            value={report.summary.estimatedLossActiveFormatted || 'R$ 0,00'}
                            hint="Perda estimada dos casos em que a Agrolend é autora: risco de não recuperar o crédito. Não é matéria de CPC 25 — a provisão deste polo é sempre zero."
                        />
                        <SummaryCard label="Provisão total" value={report.summary.provisionFormatted || 'R$ 0,00'} accent="danger" />
                        <SummaryCard
                            label="% fora do padrão sugerido"
                            value={String(report.summary.offSuggestion)}
                            hint="Casos cujo percentual de perda difere do sugerido para a classificação. Não é erro — vale conferir se foi intencional."
                        />
                        <SummaryCard
                            label="Provisão ajustada manualmente"
                            value={String(report.summary.overridden)}
                            hint="Casos onde a provisão foi sobrescrita à mão, em vez do valor calculado pela regra."
                        />
                        <SummaryCard
                            label="Encerrados ainda provisionando"
                            value={String(report.summary.closedStillProvisioning)}
                            accent={report.summary.closedStillProvisioning > 0 ? 'danger' : 'default'}
                            hint="Casos encerrados que continuam gerando provisão — a classificação CPC 25 precisa ser revista (encerrar não reclassifica automático)."
                        />
                    </div>

                    {hasData && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Cpc25BarChart
                                title="Provisão por entidade credora"
                                segments={provisionSegments}
                                emptyMessage="Nenhuma provisão reconhecida (só passivo provável provisiona)."
                            />
                            <Cpc25DonutChart
                                title="Casos por classificação"
                                segments={classificationSegments}
                            />
                        </div>
                    )}

                    <AttentionSection cases={report.attention} onOpen={(id) => navigate(`/recovery/legal-cases/${id}`)} />

                    {!hasData ? (
                        <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                            Nenhum caso para consolidar.
                        </div>
                    ) : (
                        report.entities.map((entity) => (
                            <EntityBlock
                                key={entity.creditorEntity}
                                entity={entity}
                                onDrillDown={drillDown}
                            />
                        ))
                    )}

                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                        Provisão reconhecida apenas para casos passivos classificados como prováveis (CPC 25).
                        Ativo contingente não é provisionado. Os valores são calculados a partir dos casos cadastrados.
                    </p>
                </div>
            </div>

            {showPeriodModal && (
                <ProvisionPeriodModal
                    onClose={() => setShowPeriodModal(false)}
                    onClosed={r.refresh}
                />
            )}
        </>
    );
};

export default Cpc25ReportPage;
